import API_CONFIG, { getTimelapseApiUrl } from '../config/api';

class TimelapseAPI {
  constructor(baseUrl = null) {
    // Use local backend as proxy to avoid CORS issues
    this.baseUrl = baseUrl || '/api/timelapse-proxy';
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // Handle auth errors like the main API
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new CustomEvent('auth-logout'));
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
        
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      
      // Provide more specific error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Cannot connect to timelapse server. Please check if the remote API is running.');
      }
      
      // Handle CORS errors specifically
      if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
        throw new Error('CORS error: The timelapse server is not configured to accept requests from this domain. Please contact the server administrator.');
      }
      
      throw error;
    }
  }

  // Health check
  async checkHealth() {
    return this.request(API_CONFIG.ENDPOINTS.TIMELAPSE_HEALTH);
  }

  // Get cameras
  async getCameras() {
    return this.request(API_CONFIG.ENDPOINTS.TIMELAPSE_CAMERAS);
  }

  // Get images for camera
  async getImages(camera, startDate, endDate, limit = 100) {
    const endpoint = buildApiUrl(API_CONFIG.ENDPOINTS.TIMELAPSE_IMAGES, { camera });
    const params = new URLSearchParams({
      startDate,
      endDate,
      limit: limit.toString()
    });
    return this.request(`${endpoint}?${params}`);
  }

  // Create timelapse
  async createTimelapse(params) {
    return this.request(API_CONFIG.ENDPOINTS.TIMELAPSE_CREATE, {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // Get job status
  async getJobStatus(jobId) {
    const endpoint = buildApiUrl(API_CONFIG.ENDPOINTS.TIMELAPSE_STATUS, { jobId });
    return this.request(endpoint);
  }

  // Get all jobs
  async getAllJobs() {
    return this.request(API_CONFIG.ENDPOINTS.TIMELAPSE_JOBS);
  }

  // Delete job
  async deleteJob(jobId) {
    const endpoint = buildApiUrl(API_CONFIG.ENDPOINTS.TIMELAPSE_DELETE, { jobId });
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }

  // Get download URL
  getDownloadUrl(jobId) {
    const endpoint = buildApiUrl(API_CONFIG.ENDPOINTS.TIMELAPSE_DOWNLOAD, { jobId });
    return `${this.baseUrl}${endpoint}`;
  }
}

// Helper function to build API URLs (same as in config/api.js)
function buildApiUrl(endpoint, params = {}) {
  let url = endpoint;
  
  // Replace path parameters
  Object.keys(params).forEach(key => {
    url = url.replace(`:${key}`, params[key]);
  });
  
  return url;
}

export default TimelapseAPI;
